import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ChargeAfterSdk from '../ChargeAfterSdk';
import {
  CHARGE_AFTER_WIDGET_TAGS,
  CHARGE_AFTER_WIDGET_TYPES,
} from '../../utils/common/constants';

const ChargeAfterWidget = ({ total, id, type, tag, eventListeners }) => {
  const chargeAfterWidgetElementId = `charge-after-widget-${id}`;
  const isChargeAfterLoaded = !!window.ChargeAfter;
  const baseWidgetElementIsFound = !!document.getElementById(
    chargeAfterWidgetElementId,
  );

  const [addedEventListeners, setAddedEventListeners] = useState([]);
  const [mutationObserverIsCreated, setMutationObserverIsCreated] =
    useState(false);

  useEffect(
    // Removes event listeners
    () => () => {
      addedEventListeners.forEach((addedEventListener) =>
        addedEventListener.element?.removeEventListener?.(
          addedEventListener.eventListener.event,
          addedEventListener.eventListener.callback,
          false,
        ),
      );
    },
    // Just wanted to return the cleanup function on the 1st render, that is the reason of the eslint exception
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (
      eventListeners &&
      baseWidgetElementIsFound &&
      !mutationObserverIsCreated
    ) {
      new MutationObserver(() => {
        const baseElement = document.getElementById(chargeAfterWidgetElementId);

        // on every mutation tries to add the event listener on an element contained in the widget div,
        // and if that ends successfully adds the event listener
        // to the state variable in order to remove them later.
        eventListeners?.forEach((currentEventListenerDefinition) => {
          const foundElm = baseElement.querySelector(
            currentEventListenerDefinition.selector,
          );
          if (foundElm) {
            foundElm.addEventListener(
              currentEventListenerDefinition.eventListener?.event,
              currentEventListenerDefinition.eventListener?.callback,
              false,
            );

            setAddedEventListeners((previousEventListeners) => [
              ...previousEventListeners,
              {
                element: foundElm,
                eventListener: currentEventListenerDefinition.eventListener,
              },
            ]);
          }
        });
      }).observe(document.getElementById(chargeAfterWidgetElementId), {
        childList: true,
        subtree: true,
      });
      setMutationObserverIsCreated(true);
    }
  }, [
    id,
    baseWidgetElementIsFound,
    mutationObserverIsCreated,
    eventListeners,
    chargeAfterWidgetElementId,
  ]);

  useEffect(() => {
    if (window.ChargeAfter) {
      try {
        window.ChargeAfter?.promotions?.update([{ sku: id, price: total }]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('ChargeAfter Error', e);
      }
    }
  }, [total, isChargeAfterLoaded, id]);

  return (
    <>
      <div
        id={chargeAfterWidgetElementId}
        className="ca-promotional-widget"
        data-widget-type={type}
        data-widget-item-tag={tag}
        data-widget-item-sku={id}
        data-widget-item-price={total}
      />
      <ChargeAfterSdk />
    </>
  );
};

ChargeAfterWidget.propTypes = {
  total: PropTypes.number,
  id: PropTypes.string.isRequired,
  eventListeners: PropTypes.arrayOf(
    PropTypes.shape({
      selector: PropTypes.string,
      eventListener: PropTypes.shape({
        event: PropTypes.string,
        callback: PropTypes.func,
      }),
    }),
  ),
  tag: PropTypes.oneOf(Object.values(CHARGE_AFTER_WIDGET_TAGS)),
  type: PropTypes.oneOf(Object.values(CHARGE_AFTER_WIDGET_TYPES)),
};

ChargeAfterWidget.defaultProps = {
  total: 0,
  eventListeners: undefined,
  tag: CHARGE_AFTER_WIDGET_TAGS.productLong,
  type: CHARGE_AFTER_WIDGET_TYPES.defaultTemplate,
};

export default ChargeAfterWidget;
